/**
 * Change Password Dialog
 *
 * MIGRACIÓN A TANSTACK QUERY:
 * - Usa useChangePassword para la mutación
 * - isPending reemplaza el estado isSubmitting manual
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { User } from "@/types/user";
import { useApiError } from "@/hooks/use-api-error";
import { useChangePassword } from "@/hooks/use-users";

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onSuccess: () => void;
}

const passwordSchema = z
  .object({
    new_password: z
      .string()
      .min(8, "usuarios.validation.passwordMin")
      .regex(/[A-Z]/, "usuarios.validation.passwordUppercase")
      .regex(/[0-9]/, "usuarios.validation.passwordNumber"),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "usuarios.validation.passwordMismatch",
    path: ["confirm_password"],
  });

type PasswordFormData = z.infer<typeof passwordSchema>;

export function ChangePasswordDialog({
  open,
  onOpenChange,
  user,
  onSuccess,
}: ChangePasswordDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);

  // =========================================================================
  // TANSTACK QUERY - Mutación
  // =========================================================================
  const changePasswordMutation = useChangePassword({
    onSuccess: () => {
      reset();
      onSuccess();
    },
    onError: (err) => {
      setError(getErrorMessage(err));
    },
  });

  const isSubmitting = changePasswordMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      new_password: "",
      confirm_password: "",
    },
  });

  const onSubmit = async (data: PasswordFormData) => {
    if (!user) return;

    setError(null);

    // Usar la mutación de TanStack Query
    changePasswordMutation.mutate({
      id: user.id,
      data: { new_password: data.new_password },
    });
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
      setError(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{t("usuarios.changePassword")}</DialogTitle>
          <DialogDescription>
            {t("usuarios.changePasswordDesc", { name: user?.nombre })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">{t("usuarios.newPassword")}</Label>
              <Input id="new_password" type="password" {...register("new_password")} />
              {errors.new_password && (
                <p className="text-sm text-destructive">
                  {t(errors.new_password.message as string)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">{t("usuarios.confirmPassword")}</Label>
              <Input id="confirm_password" type="password" {...register("confirm_password")} />
              {errors.confirm_password && (
                <p className="text-sm text-destructive">
                  {t(errors.confirm_password.message as string)}
                </p>
              )}
            </div>

            <p className="text-xs text-muted-foreground">{t("usuarios.passwordHint")}</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("usuarios.changePassword")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
